import React, { useState } from 'react';
import { Box, Grid, Typography, Fade, Button } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface InfoSectionProps {
    title: string;
    subtitle?: string;
    highlight?: string;
    body: string;
    image?: string;
    video?: string;
    imageLeft?: boolean;
    background?: string;
    boxWidth?: number;
    boxHeight?: number;
    button?: {
        label: string;
        href: string;
    };
    extra?: {
        title: string;
        highlight: string;
    };
}

const InfoSection: React.FC<InfoSectionProps> = ({
                                                     title,
                                                     subtitle,
                                                     highlight,
                                                     body,
                                                     image,
                                                     video,
                                                     imageLeft = false,
                                                     background = 'light',
                                                     boxWidth,
                                                     boxHeight,
                                                     button,
                                                     extra,
                                                 }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.2,
    });

    const isDark = background === 'dark';
    const textColor = isDark ? '#fff' : '#000';
    const secondaryTextColor = isDark ? '#ccc' : '#333';
    const bgColor = isDark ? '#000' : '#fff';

    const [isPlaying, setIsPlaying] = useState(false);

    const width = boxWidth || 600;
    const height = boxHeight || 350;

    const allLines = body.trim().split('\n');
    const lastLine = allLines[allLines.length - 1];
    const isSpecialCTA = lastLine.includes('See the Signals Before the Crowd');
    const textLines = isSpecialCTA ? allLines.slice(0, -1) : allLines;

    return (
        <Box ref={ref} sx={{ backgroundColor: bgColor, py: { xs: 6, md: 12 } }}>
            <Fade in={inView} timeout={1800}>
                <Grid
                    container
                    spacing={{ xs: 3, md: 0 }}
                    alignItems="center"
                    direction={imageLeft ? 'row' : 'row-reverse'}
                    justifyContent="center"
                    px={{ xs: 2, sm: 4, md: 20 }}
                >
                    <Grid item xs={12} md={6}>
                        <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, md: 0 } }}>
                            {subtitle && (
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: secondaryTextColor,
                                        letterSpacing: 1,
                                        mb: 1,
                                        fontSize: { xs: '0.9rem', md: '1rem' },
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {subtitle}
                                </Typography>
                            )}

                            <Typography
                                variant="h3"
                                sx={{
                                    color: textColor,
                                    fontSize: { xs: '2rem', md: '2.8rem' },
                                    lineHeight: 1.3,
                                    mb: highlight ? 0 : 2,
                                }}
                            >
                                {title}
                            </Typography>

                            {highlight && (
                                <Typography
                                    variant="h3"
                                    sx={{
                                        color: '#b538ff',
                                        fontWeight: 600,
                                        fontSize: { xs: '2rem', md: '2.8rem' },
                                        lineHeight: 1.3,
                                        mb: 2,
                                    }}
                                >
                                    {highlight}
                                </Typography>
                            )}

                            {textLines.map((line, idx) => {
                                const boldMatch = line.match(/^\*\*(.+)\*\*$/);
                                const content = boldMatch ? boldMatch[1] : line;
                                return (
                                    <Typography
                                        key={idx}
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '1.1rem', md: '1.25rem' },
                                            color: boldMatch ? textColor : secondaryTextColor,
                                            mb: 1.5,
                                        }}
                                    >
                                        {content}
                                    </Typography>
                                );
                            })}

                            {button && (
                                <Box mt={3}>
                                    <Button
                                        variant="outlined"
                                        href={button.href}
                                        sx={{
                                            color: '#b538ff',
                                            borderColor: '#b538ff',
                                            px: 3,
                                            py: 1.2,
                                            fontSize: '16px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: '#b538ff',
                                                color: '#fff',
                                            },
                                        }}
                                    >
                                        {button.label}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                            textAlign="left"
                            sx={{ maxWidth: width, mx: 'auto', px: 2 }}
                        >
                            {video ? (
                                isPlaying ? (
                                    <video
                                        src={video}
                                        controls
                                        autoPlay
                                        style={{
                                            width: '100%',
                                            height: `${height}px`,
                                            maxHeight: '60vh',
                                            maxWidth: '100%',
                                            objectFit: 'contain',
                                            borderRadius: '10px',
                                            marginBottom: extra ? '1.5rem' : 0,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: { xs: Math.min(300, width), md: width },
                                            height: { xs: Math.min(200, height), md: height },
                                            cursor: 'pointer',
                                            borderRadius: '10px',
                                            overflow: 'hidden',
                                            marginBottom: extra ? '1.5rem' : 0,
                                            '&:hover .play-overlay': {
                                                opacity: 1,
                                                background: 'rgba(0, 0, 0, 0.5)',
                                                '& svg': {
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                        onClick={() => setIsPlaying(true)}
                                    >
                                        <img
                                            src={image}
                                            alt="Video thumbnail"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                        <Box
                                            className="play-overlay"
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                background: 'transparent',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                opacity: 0.5,
                                                transition: 'opacity 0.3s, background 0.3s',
                                            }}
                                        >
                                            <PlayArrowIcon
                                                sx={{
                                                    fontSize: 80,
                                                    color: '#ffffff',
                                                    opacity: 0.7,
                                                    transition: 'opacity 0.3s',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )
                            ) : (
                                image && (
                                    <img
                                        src={image}
                                        alt="section visual"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: '60vh',
                                            maxWidth: width,
                                            objectFit: 'contain',
                                            display: 'block',
                                            marginBottom: extra ? '1.5rem' : 0,
                                            borderRadius: '10px',
                                        }}
                                    />
                                )
                            )}

                            {isSpecialCTA && (
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: '#7c68ff',
                                        fontWeight: 500,
                                        mt: 2,
                                        fontSize: { xs: '1.8rem', md: '2.2rem' },
                                    }}
                                >
                                    {lastLine.replace(/\*\*/g, '')}
                                </Typography>
                            )}

                            {extra && (
                                <>
                                    <Typography
                                        variant="body1"
                                        sx={{ color: isDark ? '#ccc' : '#333', mb: 1 }}
                                    >
                                        {extra.title}
                                    </Typography>
                                    {extra.highlight.split('\n').map((line, idx) => (
                                        <Typography
                                            key={idx}
                                            variant="h4"
                                            sx={{
                                                color: '#ffff33',
                                                lineHeight: 1.4,
                                                mb: 0.5,
                                            }}
                                        >
                                            {line}
                                        </Typography>
                                    ))}
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Fade>
        </Box>
    );
};

export default InfoSection;
